import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import type { OnDestroy, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  Injector,
  input,
  linkedSignal,
  Optional,
  output,
  runInInjectionContext,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import type { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import type { StaticInputsComponent } from '@core/common-legacy';
import { filterBoolean, RtComponentOutlet, TaskPriority, TaskScheduler } from '@core/common-legacy';
import { asViewRef, ToObservablePipe } from '@core/utils-legacy';

import { IconComponent } from '../../common/icon/icon.component';
import { PlaceloaderComponent } from '../../common/placeloader/placeloader.component';
import { CheckboxComponent } from '../../forms/checkbox/checkbox.component';
import { IntersectionObserverArea } from '../../utils/intersection-observer/intersection-observer-area';
import { ExpandableCellService } from '../cells/expandable-cell/expandable-cell.service';
import type { DataTableSelectionSource } from '../data-table-selection-source';
import type {
  DataTableCellContext,
  DataTableRow,
  DataTableSelectionCellContext,
  TableCellFactory,
  TableCellFactoryResult,
} from '../data-table.types';

@Component({
  selector: 'rt-data-table-row',
  templateUrl: './data-table-row.component.html',
  styleUrls: ['./data-table-row.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.selected]': 'selectedState()',
    '[class.not-selected]': '!selectedState()',
    '[class.selectable]': 'selectable()',
    '[class.clickable]': 'clickable()',
    '[class.private]': 'private()',
    '[class.hoverable]': 'hoverable()',
    '[class.cellPadding]': 'cellPadding()',
    '[class.is-zebra]': 'isZebra()',
    '[class.disabled]': 'disabled()',
    '[class.inactive]': 'inactive()',
  },
  imports: [
    CheckboxComponent,
    IconComponent,
    NgTemplateOutlet,
    PlaceloaderComponent,
    RtComponentOutlet,
    AsyncPipe,
    ToObservablePipe,
  ],
  providers: [ExpandableCellService],
})
export class DataTableRowComponent<Row extends DataTableRow> implements OnDestroy {
  public readonly index = input<number>(undefined!);
  public readonly row = input<Row>(undefined!);
  public readonly columnIds = input<string[]>(undefined!);
  public readonly cellTemplates = input<{
    [key: string]: TemplateRef<DataTableCellContext>;
  }>();
  public readonly cellFactories = input<Record<string, TableCellFactory<Row> | null>>(undefined!);
  public readonly defaultCellTemplate = input<TemplateRef<DataTableCellContext>>(undefined!);
  public readonly hoverable = input<boolean>(false);
  public readonly clickable = input<boolean>(false);
  public readonly selectable = input<boolean>(false);
  public readonly selected = input<boolean>(false);
  public readonly selectionSource = input<DataTableSelectionSource | null>(null);
  public readonly private = input<boolean>(false);
  public readonly even = input<boolean>(false);
  public readonly cellPadding = input<boolean>(true);
  public readonly isZebra = input<boolean>(true);
  public readonly disabled = input<boolean>(false);
  public readonly inactive = input<boolean>(false);

  public readonly alwaysVisible = input<boolean>(true);
  public readonly notVisibleItemHeight = input<string>('40px');
  public readonly rowDetailsTemplate = input<TemplateRef<unknown> | undefined>(undefined);
  public readonly selectionCellTemplate = input<
    TemplateRef<DataTableSelectionCellContext> | null | undefined
  >(undefined);

  public readonly rowSelect = output<boolean>();

  public readonly visible = signal<boolean>(false);
  public readonly detailsVisible = signal<boolean>(false);

  /**
   * Effective selected state. When a `selectionSource` is provided, the row
   * reads its own per-id signal so only this row re-renders when its selection
   * changes; otherwise falls back to the plain `selected` input.
   */
  public readonly selectedState = computed<boolean>(() => {
    const source = this.selectionSource();
    return source ? source.isSelected(this.row().id)() : this.selected();
  });

  public readonly cellTemplateMap = computed<Record<string, TemplateRef<DataTableCellContext>>>(
    () => {
      const columnIds = this.columnIds();
      const cellTemplates = this.cellTemplates();
      const defaultTpl = this.defaultCellTemplate();
      const result: Record<string, TemplateRef<DataTableCellContext>> = {};
      for (const columnId of columnIds) {
        result[columnId] = (cellTemplates && cellTemplates[columnId]) ?? defaultTpl;
      }
      return result;
    },
  );

  public readonly cellsContexts = linkedSignal<
    { row: Row; columnIds: string[]; rowIndex: number; rowEven: boolean },
    Record<string, DataTableCellContext<Row>>
  >({
    source: () => ({
      row: this.row(),
      columnIds: this.columnIds(),
      rowIndex: this.index(),
      rowEven: this.even(),
    }),
    computation: ({ row, columnIds, rowIndex, rowEven }, prev) => {
      const rowChanged = !prev || prev.source.row !== row;
      const prevValue = prev?.value ?? {};
      const result: Record<string, DataTableCellContext<Row>> = {};
      for (let i = 0; i < columnIds.length; i++) {
        const id = columnIds[i];
        const existing = prevValue[id];
        result[id] =
          !rowChanged && existing
            ? existing
            : { columnId: id, row, rowIndex, rowEven, cellIndex: i };
      }
      return result;
    },
  });

  public readonly cells = linkedSignal<
    {
      row: Row;
      columnIds: string[];
      factories: Record<string, TableCellFactory<Row> | null>;
    },
    Array<TableCellFactoryResult<StaticInputsComponent> | undefined>
  >({
    source: () => ({
      row: this.row(),
      columnIds: this.columnIds(),
      factories: this.cellFactories(),
    }),
    computation: ({ row, columnIds, factories }, prev) => {
      const reuse = new Map<string, TableCellFactoryResult<StaticInputsComponent> | undefined>();
      if (prev && prev.source.row === row) {
        const prevColumnIds = prev.source.columnIds;
        const prevFactories = prev.source.factories;
        const prevCells = prev.value;
        for (let i = 0; i < prevColumnIds.length; i++) {
          const id = prevColumnIds[i];
          if (factories[id] === prevFactories[id]) {
            reuse.set(id, prevCells[i]);
          }
        }
      }

      // Read contexts without creating a reactive dependency — cellsContexts is
      // driven by the same (row, columnIds) source as cells, so reading the
      // current value here is guaranteed to be consistent with our own inputs.
      const contexts = untracked(this.cellsContexts);
      return runInInjectionContext(this.injector, () =>
        columnIds.map(id => (reuse.has(id) ? reuse.get(id) : factories[id]?.(contexts[id]))),
      );
    },
  });

  public readonly selectionCellContext = computed<DataTableSelectionCellContext | null>(() => {
    if (!this.selectable() || !this.selectionCellTemplate()) return null;
    return {
      row: this.row(),
      selected: this.selectedState(),
      handleSelect: selected => this.rowSelect.emit(selected),
    };
  });

  private hiddenEl = viewChild<ElementRef>('hiddenEl');
  private ngOnDestroy$ = new Subject<void>();
  private areaSub: Subscription | null = null;

  constructor(
    @Optional() private area: IntersectionObserverArea,
    private elRef: ElementRef,
    private cdRef: ChangeDetectorRef,
    private taskScheduler: TaskScheduler,
    private injector: Injector,
  ) {
    effect(() => {
      const alwaysVisible = this.alwaysVisible();
      const hiddenElRef = this.hiddenEl();

      if (alwaysVisible || !this.area?.serviceAvailable) {
        untracked(() => this.visible.set(true));
        return;
      }

      if (!hiddenElRef) return;

      untracked(() => this.drawWhenVisible(hiddenElRef));
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public trackByColumnId(index: number, columnId: string): string {
    return columnId;
  }

  public toggleDetailsRow(): void {
    this.detailsVisible.update(v => !v);
  }

  private drawWhenVisible(hiddenEl: ElementRef): void {
    if (!this.area?.serviceAvailable) return;

    if (this.areaSub) {
      this.areaSub.unsubscribe();
      this.areaSub = null;
    }

    this.areaSub = this.area
      .intersectionChange(hiddenEl)
      .pipe(
        map(({ visible }) => visible),
        filterBoolean,
        take(1),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe({
        next: () => this.show(),
        error: () => this.show(),
      });
  }

  private show(): void {
    this.taskScheduler.scheduleMacrotask(TaskPriority.High, () => {
      if (asViewRef(this.cdRef).destroyed || this.visible()) return;
      this.visible.set(true);
    });
  }
}
